import { apiClient } from '@/lib/api/client';
import { API_ENDPOINTS } from '@/lib/api/config';
import type { SearchParams, SearchResponse } from '../types';

const { SEARCH } = API_ENDPOINTS;

/**
 * apiClient.get returns ApiResponse<SearchResponse>; unwrap here so the
 * query/queries layer deals in domain types (SearchResponse) rather than
 * the transport envelope.
 */
export const searchApi = {
  query: async (params: SearchParams): Promise<SearchResponse> => {
    // Base request — `apiClient.get` serializes arrays as `key[]=val` and the
    // flattened `form_*[id][...]` keys below as-is.
    const request: Record<string, unknown> = {
      q: params.q,
      ...(params.entity_types ? { entity_types: params.entity_types } : {}),
      ...(params.page !== undefined ? { page: params.page } : {}),
      ...(params.per_page !== undefined ? { per_page: params.per_page } : {}),
      ...(params.understand ? { understand: 1 } : {}),
      // Multi-value structured filters — spread as arrays.
      ...(params.current_stage ? { current_stage: params.current_stage } : {}),
      ...(params.status ? { status: params.status } : {}),
      ...(params.document_type ? { document_type: params.document_type } : {}),
      // Booleans — only when explicitly defined.
      ...(params.is_rejected !== undefined
        ? { is_rejected: params.is_rejected }
        : {}),
      ...(params.in_box !== undefined ? { in_box: params.in_box } : {}),
      ...(params.not_in_box !== undefined
        ? { not_in_box: params.not_in_box }
        : {}),
      // Date / numeric ranges.
      ...(params.created_from ? { created_from: params.created_from } : {}),
      ...(params.created_to ? { created_to: params.created_to } : {}),
      ...(params.min_page_count !== undefined
        ? { min_page_count: params.min_page_count }
        : {}),
      ...(params.max_page_count !== undefined
        ? { max_page_count: params.max_page_count }
        : {}),
      ...(params.dynamic_form_id !== undefined
        ? { dynamic_form_id: params.dynamic_form_id }
        : {})
    };

    // Flatten dynamic-form selection filters into `form_responses_filter[{fieldId}]`
    // keys. The value is an array, and `apiClient.get` appends the `[]` itself
    // (client.ts) → `form_responses_filter[{fieldId}][]=val`. Do NOT add `[]` to
    // the key here or it double-brackets into `[{fieldId}][][]` (a 3-level array
    // the backend rejects).
    if (params.form_responses_filter) {
      for (const [fieldId, values] of Object.entries(
        params.form_responses_filter
      )) {
        if (values.length > 0) {
          request[`form_responses_filter[${fieldId}]`] = values;
        }
      }
    }

    // Flatten dynamic-form date filters into
    // `form_date_filter[{fieldId}][from|to]` keys.
    if (params.form_date_filter) {
      for (const [fieldId, range] of Object.entries(params.form_date_filter)) {
        if (range.from) {
          request[`form_date_filter[${fieldId}][from]`] = range.from;
        }
        if (range.to) {
          request[`form_date_filter[${fieldId}][to]`] = range.to;
        }
      }
    }

    const response = await apiClient.get<SearchResponse>(SEARCH, request);
    return response.data;
  }
};
